import { ScanRecipeResults } from '@cf-shared/scan-recipe-interface';

// Hand-authored sample scan results for the test-mode MOCK path (and any mock-based
// E2E scenario): they let a tester exercise the results screen — ingredient matching,
// the matched/unmatched UX, the editable name + servings, the instructions, and the
// round-trip apply back to Create Recipe — in a browser, with no camera, no AI
// backend, and no provider tokens. These are injected directly as the post-AI result,
// so there is no real photo behind them; only the AI call is skipped. The ingredient
// names below run through the SAME real food-matching pipeline the live scan uses, so
// which rows land matched vs unmatched depends on the local food DB. Names are written
// generic and DB-searchable, exactly as the AI prompt asks the model to produce. The
// first two are hand-authored (one sweet/baking, one savoury) for ingredient variety;
// the third is a verbatim capture of a real scan (a handwritten recipe page + a plated
// dish photo) so the mock matches what the live model actually returns. The captured
// scan also picked the dish photo as the recipe image (recipeImageIndex 1), but mocks
// have no photos so no image card shows for it.

export interface MockScannedRecipe {
  /** menu label on the dev MOCK picker */
  label: string;
  recipe: ScanRecipeResults;
}

export const MOCK_SCANNED_RECIPES: MockScannedRecipe[] = [
  {
    label: 'Banana Bread',
    recipe: {
      name: 'Banana Bread',
      servings: 8,
      instructions:
        '1. Preheat the oven to 175°C (350°F) and grease a loaf tin.\n' +
        '2. Mash the bananas and mix with the melted butter.\n' +
        '3. Stir in the sugar, egg, and vanilla.\n' +
        '4. Fold in the flour, baking soda, and salt until just combined.\n' +
        '5. Pour into the tin and bake for 50-60 minutes.',
      ingredients: [
        { name: 'banana', estimatedGrams: 450, rawQuantity: '3 ripe bananas', confidence: 0.95 },
        { name: 'butter', estimatedGrams: 75, rawQuantity: '1/3 cup melted butter', confidence: 0.9 },
        { name: 'sugar', estimatedGrams: 150, rawQuantity: '3/4 cup sugar', confidence: 0.9 },
        { name: 'egg', estimatedGrams: 50, rawQuantity: '1 egg', confidence: 0.95 },
        { name: 'all-purpose flour', estimatedGrams: 190, rawQuantity: '1 1/2 cups flour', confidence: 0.85 },
        { name: 'baking soda', estimatedGrams: 5, rawQuantity: '1 tsp baking soda', confidence: 0.8 },
        { name: 'salt', estimatedGrams: 2, rawQuantity: 'a pinch of salt', confidence: 0.8 },
        { name: 'walnuts', estimatedGrams: 60, rawQuantity: '1/2 cup chopped walnuts', confidence: 0.7 },
      ],
    },
  },
  {
    label: 'Chicken Stir Fry',
    recipe: {
      name: 'Chicken Stir Fry',
      servings: 4,
      instructions:
        '1. Slice the chicken breast and season lightly.\n' +
        '2. Heat the sesame oil in a wok over high heat.\n' +
        '3. Stir-fry the chicken until golden, then set aside.\n' +
        '4. Stir-fry the broccoli, garlic, and ginger for 2 minutes.\n' +
        '5. Return the chicken, add the soy sauce, and toss. Serve over rice.',
      ingredients: [
        { name: 'chicken breast', estimatedGrams: 500, rawQuantity: '2 chicken breasts', confidence: 0.95 },
        { name: 'broccoli', estimatedGrams: 300, rawQuantity: '1 head broccoli', confidence: 0.9 },
        { name: 'white rice', estimatedGrams: 280, rawQuantity: '1 1/2 cups rice', confidence: 0.85 },
        { name: 'soy sauce', estimatedGrams: 45, rawQuantity: '3 tbsp soy sauce', confidence: 0.9 },
        { name: 'garlic', estimatedGrams: 15, rawQuantity: '3 cloves garlic', confidence: 0.85 },
        { name: 'ginger', estimatedGrams: 10, rawQuantity: '1 tsp grated ginger', confidence: 0.8 },
        { name: 'sesame oil', estimatedGrams: 14, rawQuantity: '1 tbsp sesame oil', confidence: 0.85 },
      ],
    },
  },
  {
    // Verbatim from a real gemini-2.5-flash scan of the handwritten recipe page + dish
    // photo (tmp/scan-recipe-local.cjs). The page states no serving count, so the model
    // (correctly) omitted servings.
    label: 'Creamy Garlic Parmesan Chicken Pasta',
    recipe: {
      name: 'Creamy Garlic Parmesan Chicken Pasta',
      instructions:
        '1. Cook pasta according to package instructions. Reserve ½ cup pasta water, then drain and set aside.\n' +
        '2. Season chicken with salt, pepper, and Italian seasoning.\n' +
        '3. Heat olive oil in a large skillet over medium heat. Cook chicken for 6-7 minutes per side or until cooked through. Remove and let rest, then slice.\n' +
        '4. In the same skillet, add garlic and sauté for 30 seconds until fragrant.\n' +
        '5. Pour in chicken broth and heavy cream. Bring to a simmer.\n' +
        '6. Stir in parmesan cheese until melted and smooth.\n' +
        '7. Add pasta and sliced chicken to the sauce. Toss to coat. Add reserved pasta water a little at a time if needed.\n' +
        '8. Garnish with fresh parsley and extra parmesan. Serve warm!',
      ingredients: [
        { name: 'chicken breast', estimatedGrams: 370, rawQuantity: '2', confidence: 0.9 },
        { name: 'pasta', estimatedGrams: 225, rawQuantity: '8 oz (225g)', confidence: 1 },
        { name: 'olive oil', estimatedGrams: 28, rawQuantity: '2 tbsp', confidence: 1 },
        { name: 'garlic', estimatedGrams: 20, rawQuantity: '4 cloves', confidence: 0.9 },
        { name: 'heavy cream', estimatedGrams: 240, rawQuantity: '1 cup', confidence: 1 },
        { name: 'chicken broth', estimatedGrams: 240, rawQuantity: '1 cup', confidence: 1 },
        { name: 'grated parmesan cheese', estimatedGrams: 100, rawQuantity: '1 cup', confidence: 0.9 },
        { name: 'Italian seasoning', estimatedGrams: 2, rawQuantity: '1 tsp', confidence: 1 },
        { name: 'fresh parsley', estimatedGrams: 5, rawQuantity: 'chopped (for garnish)', confidence: 0.8 },
      ],
    },
  },
];
